import { JSX, ScriptHTMLAttributes, useEffect, useRef } from 'react';

import { ScriptEnvironment } from 'app/components/scripts/helpers';
import { keys } from 'app/config/keys';

/* eslint-disable react/no-unused-prop-types*/

const LoadCache = new Set();

export interface ScriptProps extends ScriptHTMLAttributes<HTMLScriptElement> {
  environment: ScriptEnvironment;
  id: string;
  onReady?: (() => void) | null;
  serviceName: string;
  strategy: 'afterInteractive' | 'beforeInteractive';
}

const scriptEnvironmentMap: Record<ScriptEnvironment, boolean> = {
  [ScriptEnvironment.ProductionOnly]: keys.NODE_ENV === 'production',
  [ScriptEnvironment.DevelopmentOnly]: keys.__DEV__,
  [ScriptEnvironment.AllEnvironments]: true,
};

const skipReasonMap: Record<ScriptEnvironment, string> = {
  [ScriptEnvironment.ProductionOnly]: 'non-production environment',
  [ScriptEnvironment.DevelopmentOnly]: 'production environment',
  [ScriptEnvironment.AllEnvironments]: 'environment restriction',
};

/**
 * Checks if script should run in environment.
 * Creates and injects a script element into the DOM.
 * For inline scripts (dangerouslySetInnerHTML), executes immediately and marks as loaded.
 * Uses LoadCache to prevent duplicate script injection based on script ID.
 */
const loadScript = (props: ScriptProps): void => {
  const {
    dangerouslySetInnerHTML,
    environment,
    id,
    onReady = null,
    serviceName,
    strategy,
  } = props;

  // Check if script should be loaded based on environment
  if (keys.NODE_ENV !== 'test' && !scriptEnvironmentMap[environment]) {
    const reason = skipReasonMap[environment];
    console.info(`Skipping ${serviceName} in ${reason}`);

    return;
  }

  const cacheKey = id;

  // Script has already loaded
  if (cacheKey && LoadCache.has(cacheKey)) {
    return;
  }

  /** Execute after the script first loaded */
  const afterLoad = () => {
    // Run onReady for the first time after load event
    if (onReady) {
      onReady();
    }
    // add cacheKey to LoadCache when load successfully
    LoadCache.add(cacheKey);
  };

  const el = document.createElement('script');

  if (dangerouslySetInnerHTML) {
    // Casting since lib.dom.d.ts doesn't have TrustedHTML yet.
    el.innerHTML = (dangerouslySetInnerHTML.__html as string) || '';

    afterLoad();
  }

  el.setAttribute('data-nscript', strategy);
  document.body.appendChild(el);
};

const Script = (props: ScriptProps): JSX.Element | null => {
  const hasOnReadyEffectCalled = useRef(false);

  useEffect(() => {
    const cacheKey = props.id;
    if (!hasOnReadyEffectCalled.current) {
      // Run onReady if script has loaded before but component is re-mounted
      if (props.onReady && cacheKey && LoadCache.has(cacheKey)) {
        props.onReady();
      }

      hasOnReadyEffectCalled.current = true;
    }
  }, [props]);

  const hasLoadScriptEffectCalled = useRef(false);

  // 2) Load 'afterInteractive' script in a useEffect to ensure it runs after render.
  useEffect(() => {
    if (!hasLoadScriptEffectCalled.current) {
      if (props.strategy === 'afterInteractive') {
        loadScript(props);
      }

      hasLoadScriptEffectCalled.current = true;
    }
  }, [props]);

  // 1) Immediately run 'beforeInteractive' script during render phase.
  if (props.strategy === 'beforeInteractive') {
    loadScript(props);
  }

  return null;
};

export default Script;
